/**
 * Time Entry Defaults Manager
 * Handles intelligent defaults for manual time entry creation
 */

class TimeEntryDefaults {
  static STORAGE_KEY = 'ivoliq_time_entry_defaults';
  
  // Fixed time defaults (not changeable)
  static TIME_DEFAULTS = {
    startTime: '09:00',
    endTime: '17:00'
  };
  
  /**
   * Load saved defaults from localStorage
   */
  static loadDefaults() {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading time entry defaults:', error);
      return {};
    }
  }
  
  /**
   * Save last used project (from timer or manual entry)
   */
  static saveLastUsedProject(projectId, projectName) {
    if (!projectId || !projectName) return;
    
    const defaults = this.loadDefaults();
    defaults.lastUsedProject = {
      id: projectId,
      name: projectName,
      timestamp: Date.now()
    };
    
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaults));
    } catch (error) {
      console.error('Error saving last used project:', error);
    }
  }
  
  /**
   * Generate default values for manual entry creation
   */
  static getManualEntryDefaults(availableProjects = []) {
    const defaults = this.loadDefaults();
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Check if last used project is still available
    let defaultProjectId = '';
    if (defaults.lastUsedProject) {
      const projectExists = availableProjects.find(
        p => p.id === defaults.lastUsedProject.id && p.status === 'active'
      );
      if (projectExists) {
        defaultProjectId = defaults.lastUsedProject.id;
      } else {
        // Clean up invalid project
        this.clearLastUsedProject();
      }
    }
    
    return {
      project_id: defaultProjectId,
      description: '',
      start_time: `${todayStr}T${this.TIME_DEFAULTS.startTime}`,
      end_time: `${todayStr}T${this.TIME_DEFAULTS.endTime}`
    };
  }
  
  /**
   * Clear invalid last used project
   */
  static clearLastUsedProject() {
    const defaults = this.loadDefaults();
    delete defaults.lastUsedProject;
    
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(defaults));
    } catch (error) {
      console.error('Error clearing last used project:', error);
    }
  }
  
  /**
   * Get last used project info
   */
  static getLastUsedProject() {
    const defaults = this.loadDefaults();
    return defaults.lastUsedProject || null;
  }
  
  /**
   * Calculate default duration (8h 0m)
   */
  static calculateDefaultDuration() {
    const start = new Date(`2000-01-01T${this.TIME_DEFAULTS.startTime}:00`);
    const end = new Date(`2000-01-01T${this.TIME_DEFAULTS.endTime}:00`);
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  }
}

export default TimeEntryDefaults;
